import React, { useEffect, useState } from 'react';
import {
  Box, Checkbox, Select, Text, Wrap, WrapItem, useColorModeValue,
} from '@chakra-ui/react';
import { NIL } from 'uuid';
import { useClassifications, useProjectClassifications, ClassificationField } from '../hooks/useClassifications';

// item 163 — the per-project classification pickers (Job Type / Brand / Runway …).
// Single-select fields render a dropdown; multi-select render checkboxes. Values are
// independent of the project row, so they auto-save on change (works even before the
// project form is submitted). For a brand-new project, set them after the first save.
const ProjectClassificationFields: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { fields } = useClassifications();
  const { optionIds, setForProject } = useProjectClassifications(projectId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');

  useEffect(() => {
    setSelected(new Set(optionIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionIds.join(',')]);

  if (!projectId || projectId === NIL) {
    return <Text fontSize="sm" color={subColor}>Save the project first to assign classifications.</Text>;
  }
  if (fields.length === 0) {
    return <Text fontSize="sm" color={subColor}>No classifications defined yet (add them in Settings).</Text>;
  }

  const commit = (next: Set<string>) => { setSelected(next); setForProject(Array.from(next)); };

  const selectSingle = (field: ClassificationField, optId: string) => {
    const next = new Set(selected);
    field.options.forEach((o) => next.delete(o.id));
    if (optId) next.add(optId);
    commit(next);
  };
  const toggleMulti = (optId: string) => {
    const next = new Set(selected);
    if (next.has(optId)) next.delete(optId); else next.add(optId);
    commit(next);
  };

  return (
    <Box>
      {fields.map((f) => (
        <Box key={f.id} mb={4}>
          <Text fontSize="sm" fontWeight="semibold" mb={1}>{f.name}</Text>
          {f.allowMultiple ? (
            <Wrap spacing={4}>
              {f.options.map((o) => (
                <WrapItem key={o.id}>
                  <Checkbox isChecked={selected.has(o.id)} onChange={() => toggleMulti(o.id)}>{o.label}</Checkbox>
                </WrapItem>
              ))}
            </Wrap>
          ) : (
            <Select
              size="sm"
              maxW="18rem"
              value={f.options.find((o) => selected.has(o.id))?.id ?? ''}
              onChange={(e) => selectSingle(f, e.target.value)}
            >
              <option value="">— none —</option>
              {f.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </Select>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default ProjectClassificationFields;
